//Modules
import { Fragment, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Dialog,
  Disclosure,
  Menu,
  Popover,
  Transition,
} from "@headlessui/react";
import { XIcon, FireIcon } from "@heroicons/react/outline";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/solid";

//Data
import { findBrandByVendorId } from "../../utils/data/brands";
import { capitalize } from "../../utils/products/utils_products";

//Lib & Utils
import { getFileExt } from "../../utils/products/utils_products";
import {
  getAllEquipment,
  getEquipmentItems,
} from "../../lib/methodCRM/method_equipment";

//Components
import Layout from "../../components/shared/layout/general";

//Hooks
import { usePrice } from "../../lib/hooks/price";
import { useAuth } from "../../lib/hooks/auth";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Content = ({ items, equipment, categories }) => {
  const [open, setOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [activeSort, setActiveSort] = useState([]);

  const { getPrice } = usePrice();
  const { authObject } = useAuth();

  const sortOptions = ["A-Z", "Price: High to Low", "Price: Low to High"];

  // Ability to filter by the following groups
  const filters = [
    {
      id: "brand",
      name: "Brand",
      options: categories,
    },
  ];

  function toggleFilter(value) {
    let a = [...activeFilters];
    if (activeFilters.find((b) => b === value)) {
      a.pop(a.indexOf(value));
      setActiveFilters(a);
    } else {
      a.push(value);
      setActiveFilters(a);
    }
  }

  function filterFunc(item) {
    // if no filters are toggled don't filter
    if (!activeFilters.length) {
      return true;
    }
    // filter by the stable vendor RecordID (the brand filter's key)
    return activeFilters.find((b) => b === item.PrefVendor_RecordID);
  }

  function toggleSort(option) {
    setActiveSort([option]);
  }

  function sortFunc(a, b) {
    let aPrice = getPrice(a.FullName, a.eCommerceCategory, a.SalesPrice);
    let bPrice = getPrice(b.FullName, b.eCommerceCategory, b.SalesPrice);
    if (activeSort.length !== 0) {
      const sort = activeSort[0];
      if (sort === "A-Z") {
        if (a.eCommerceStoreName < b.eCommerceStoreName) return -1;
        if (a.eCommerceStoreName > b.eCommerceStoreName) return 1;
        return 0;
      }
      if (sort === "Price: High to Low") {
        if (aPrice < bPrice) return 1;
        if (aPrice > bPrice) return -1;
        return 0;
      }
      if (sort === "Price: Low to High") {
        if (aPrice < bPrice) return -1;
        if (aPrice > bPrice) return 1;
        return 0;
      }
    } else {
      return 0;
    }
  }

  return (
    <div className="bg-white">
      {/* Mobile filter dialog */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-40 sm:hidden"
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="ml-auto relative max-w-xs w-full h-full bg-white shadow-xl py-4 pb-12 flex flex-col overflow-y-auto">
              <div className="px-4 flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                <button
                  type="button"
                  className="-mr-2 w-10 h-10 bg-white p-2 rounded-md flex items-center justify-center text-gray-400"
                  onClick={() => setOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Filters */}
              <form className="mt-4">
                {filters.map((section) => (
                  <Disclosure
                    as="div"
                    key={section.name}
                    className="border-t border-gray-200 px-4 py-6"
                  >
                    {({ open }) => (
                      <>
                        <h3 className="-mx-2 -my-3 flow-root">
                          <Disclosure.Button className="px-2 py-3 bg-white w-full flex items-center justify-between text-sm text-gray-400">
                            <span className="font-medium text-gray-900">
                              {section.name}
                            </span>
                            <span className="ml-6 flex items-center">
                              <ChevronDownIcon
                                className={classNames(
                                  open ? "-rotate-180" : "rotate-0",
                                  "h-5 w-5 transform"
                                )}
                                aria-hidden="true"
                              />
                            </span>
                          </Disclosure.Button>
                        </h3>
                        <Disclosure.Panel className="pt-6">
                          <div className="space-y-6">
                            {section.options.map((option, optionIdx) => (
                              <div
                                key={option.id}
                                className="flex items-center"
                              >
                                <input
                                  id={`filter-mobile-${section.id}-${optionIdx}`}
                                  name={`${section.id}[]`}
                                  defaultValue={option.id}
                                  type="checkbox"
                                  onChange={() => toggleFilter(option.id)}
                                  checked={Boolean(
                                    activeFilters.find(
                                      (a) => a === option.id
                                    )
                                  )}
                                  className="h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500"
                                />
                                <label
                                  htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                  className="ml-3 text-sm text-gray-500"
                                >
                                  {findBrandByVendorId(option.id)?.displayName || option.value}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                ))}
              </form>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>

      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8 flex justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
            {capitalize(equipment)}
          </h1>
          {/* <p className="mt-4 max-w-full text-sm text-gray-700">Equipment</p> */}
        </div>
        {/* ================== */}
        {/*  An idea */}
        {/* ================== */}
        {/* <div className="flex justify-between items-end w-1/2">
          <div className="flex h-full items-end">
            <span className="text-blue-400 text-4xl pr-2">10</span>
            <div className="flex flex-col">
              <p className="text-sm text-gray-700">unique</p>
              <p className="text-sm text-gray-700">products</p>
            </div>
          </div>
          <div className="text-3xl text-gray-300">|</div>
          <div className="flex h-full items-end">
            <span className="text-blue-400 text-4xl pr-2">8</span>
            <div className="flex flex-col">
              <p className="text-sm text-gray-700">trusted</p>
              <p className="text-sm text-gray-700">brands</p>
            </div>
          </div>
          <div className="text-3xl text-gray-300">|</div>
          <div className="flex h-full items-end">
            <span className="text-blue-400 text-4xl pr-2">100%</span>
            <div className="flex flex-col">
              <p className="text-sm text-gray-700">quality</p>
              <p className="text-sm text-gray-700">guaranteed</p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Filters */}
      <section aria-labelledby="filter-heading">
        <h2 id="filter-heading" className="sr-only">
          Filters
        </h2>
        {/* SORT */}
        <div className="relative bg-white border-b border-gray-200 pb-4">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between sm:px-6 lg:px-8">
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                  Sort
                  <ChevronDownIcon
                    className="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                </Menu.Button>
              </div>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-left absolute left-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1 hover:cursor-pointer">
                    {sortOptions.map((option) => (
                      <Menu.Item key={option}>
                        {({ active }) => (
                          <span
                            onClick={() => toggleSort(option)}
                            className={classNames(
                              Boolean(activeSort.find((a) => a === option))
                                ? "font-medium text-gray-900"
                                : "text-gray-500",
                              active ? "bg-gray-100" : "",
                              "block px-4 py-2 text-sm"
                            )}
                          >
                            {option}
                          </span>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            <button
              type="button"
              className="inline-block text-sm font-medium text-gray-700 hover:text-gray-900 sm:hidden"
              onClick={() => setOpen(true)}
            >
              Filters
            </button>
            {/* Filters */}
            <div className="hidden sm:block">
              <div className="flow-root">
                <Popover.Group className="-mx-4 flex items-center divide-x divide-gray-200">
                  {filters.map((section, sectionIdx) => (
                    <Popover
                      key={section.name}
                      className="px-4 relative inline-block text-left"
                    >
                      <Popover.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                        <span>{section.name}</span>

                        <span className="ml-1.5 rounded py-0.5 px-1.5 bg-gray-200 text-xs font-semibold text-gray-700 tabular-nums">
                          {section.options.length}
                        </span>
                        <ChevronDownIcon
                          className="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Popover.Panel className="origin-top-right absolute right-0 mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <form className="space-y-4">
                            {section.options.map((option, optionIdx) => (
                              <div
                                key={option.id}
                                className="flex items-center"
                              >
                                <input
                                  id={`filter-${section.id}-${optionIdx}`}
                                  name={`${section.id}[]`}
                                  defaultValue={option.id}
                                  type="checkbox"
                                  onChange={() => toggleFilter(option.id)}
                                  checked={Boolean(
                                    activeFilters.find(
                                      (a) => a === option.id
                                    )
                                  )}
                                  className="h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500"
                                />
                                <label
                                  htmlFor={`filter-${section.id}-${optionIdx}`}
                                  className="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap"
                                >
                                  {findBrandByVendorId(option.id)?.displayName || option.value}
                                </label>
                              </div>
                            ))}
                          </form>
                        </Popover.Panel>
                      </Transition>
                    </Popover>
                  ))}
                </Popover.Group>
              </div>
            </div>
          </div>
        </div>

        {/* Active filters */}
        <div className="bg-gray-100">
          <div className="max-w-7xl mx-auto py-3 px-4 sm:flex sm:items-center sm:px-6 lg:px-8">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Filters
              <span className="sr-only">, active</span>
            </h3>

            <div
              aria-hidden="true"
              className="hidden w-px h-5 bg-gray-300 sm:block sm:ml-4"
            />

            <div className="mt-2 sm:mt-0 sm:ml-4">
              <div className="-m-1 flex flex-wrap items-center">
                {activeFilters.map((activeFilter) => {
                  const filterLabel =
                    findBrandByVendorId(activeFilter)?.displayName ||
                    categories.find((c) => c.id === activeFilter)?.value ||
                    activeFilter;
                  return (
                  <span
                    key={activeFilter}
                    className="m-1 inline-flex rounded-full border border-gray-200 items-center py-1.5 pl-3 pr-2 text-sm font-medium bg-white text-gray-900"
                  >
                    <span>
                      {filterLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleFilter(activeFilter)}
                      className="flex-shrink-0 ml-1 h-4 w-4 p-1 rounded-full inline-flex text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                    >
                      <span className="sr-only">
                        Remove filter for {filterLabel}
                      </span>
                      <svg
                        className="h-2 w-2"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 8 8"
                      >
                        <path
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          d="M1 1l6 6m0-6L1 7"
                        />
                      </svg>
                    </button>
                  </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* {CATALOG CODE} */}
      <div className="max-w-2xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:max-w-7xl lg:px-8">
        <h2 className="sr-only">Products</h2>

        <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-6 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-8">
          {items
            .sort((a, b) => sortFunc(a, b))
            .filter((item) => filterFunc(item))
            .map((item, i) => (
              <Link key={i} href={"/" + item.eCommerceURL}>
                <div className="group">
                  <div className="w-full aspect-w-1 aspect-h-1 bg-gray-200 rounded-lg overflow-hidden xl:aspect-w-7 xl:aspect-h-8">
                    {item.WDCatalogImage ? (
                      <Image
                        src={`data:image/${getFileExt(
                          item.WDCatalogImageFileName
                        )};base64, ${item.WDCatalogImage}`}
                        alt={item.eCommerceStoreName}
                        layout="fill"
                        objectFit="cover"
                        objectPosition={"50% 50%"}
                        className="w-full h-full object-center object-cover group-hover:opacity-75"
                      />
                    ) : (
                      <FireIcon className="w-full h-full object-center object-cover p-4" />
                    )}
                  </div>
                  <h3 className="mt-4 text-sm text-gray-700">
                    {item.eCommerceStoreName}
                  </h3>
                  <div className="flex mt-1 text-lg font-medium text-gray-900">
                    <p
                      className={classNames(
                        getPrice(
                          item.FullName,
                          item.eCommerceCategory,
                          item.SalesPrice
                        ) !== item.SalesPrice
                          ? "line-through text-gray-600"
                          : "",
                        " "
                      )}
                    >
                      ${item.SalesPrice}
                    </p>
                    {/* only render the discount price if authed */}
                    {authObject ? (
                      <p className="ml-2 italic">
                        {getPrice(
                          item.FullName,
                          item.eCommerceCategory,
                          item.SalesPrice
                        ) !== item.SalesPrice
                          ? `$${getPrice(
                              item.FullName,
                              item.eCommerceCategory,
                              item.SalesPrice
                            )}`
                          : ``}
                      </p>
                    ) : null}
                  </div>
                </div>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
};

const Equipment = ({ items, equipment, categories }) => (
  <Layout>
    <Content items={items} equipment={equipment} categories={categories} />
  </Layout>
);

export default Equipment;

export async function getStaticPaths() {
  // Retrieve equipment names from local data
  const res = getAllEquipment();

  // Use equipment names as paths for pre-rendered pages
  const paths = res.map((url) => ({
    params: { equipment: url },
  }));

  // Only render these paths at build time.
  // any other path will return a 404 {fallback: false}
  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const res = await getEquipmentItems(params);
  return {
    props: {
      items: res.items,
      equipment: res.equipment,
      categories: res.categories,
    },
    revalidate: 60,
  };
}
